import {jsonSafeStorage} from '@github-ui/safe-storage'

/** A comment entry as sent in the apply-suggestions request body. */
export type UnifiedComment = {
  id: number
  suggestionIndex?: number
}

const SUGGESTION_OPERATION_STORAGE_SUFFIX = '-active-suggestion-operation'

/** Current storage payload schema version. Bump on any breaking change to the payload. */
const SUGGESTION_OPERATION_STORAGE_VERSION = 1

type RetainedOperation = {
  identity: string
  id: string
}

type SuggestionOperationPayload = {
  version: typeof SUGGESTION_OPERATION_STORAGE_VERSION
  operations: RetainedOperation[]
}

export function getSuggestionOperationStorageKey(pullRequestPath: string): string {
  return `${pullRequestPath}${SUGGESTION_OPERATION_STORAGE_SUFFIX}`
}

const storage = jsonSafeStorage<Partial<SuggestionOperationPayload>>('sessionStorage')

/**
 * Stable comment order shared by the request body and the identity. The server fingerprints the
 * array as it receives it, so sending an order that differs from the one the identity was built
 * from would produce a matching key with a mismatched fingerprint (`IDEMPOTENCY_KEY_REUSED`).
 */
export function sortSuggestionOperationComments(comments: readonly UnifiedComment[]): UnifiedComment[] {
  return [...comments].sort((a, b) => a.id - b.id || (a.suggestionIndex ?? -1) - (b.suggestionIndex ?? -1))
}

/**
 * Identifies the operation by the same semantic fields the server fingerprints. `expectedHeadOid`
 * is a precondition on the request rather than part of what is being applied, so it is excluded.
 */
export function buildSuggestionOperationIdentity(comments: readonly UnifiedComment[], message: string): string {
  return JSON.stringify({comments: sortSuggestionOperationComments(comments), message})
}

function isRetainedOperation(value: unknown): value is RetainedOperation {
  if (!value || typeof value !== 'object') return false
  const operation = value as Record<string, unknown>
  return typeof operation.identity === 'string' && typeof operation.id === 'string' && operation.id.length > 0
}

const retainedOperationsCache = new Map<string, RetainedOperation[]>()

function readStoredOperations(key: string): RetainedOperation[] {
  const stored = storage.getItem(key)
  if (!stored || typeof stored !== 'object') return []
  if (stored.version !== SUGGESTION_OPERATION_STORAGE_VERSION) return []
  if (!Array.isArray(stored.operations)) return []
  return stored.operations.filter(isRetainedOperation)
}

function loadRetainedOperations(key: string): RetainedOperation[] {
  const cached = retainedOperationsCache.get(key)
  if (cached) return cached

  const stored = readStoredOperations(key)
  retainedOperationsCache.set(key, stored)
  return stored
}

/**
 * Returns the idempotency key for this operation, minting and persisting one the first time it
 * is seen. Retries of the same batch reuse the key across reloads and remounts so the server can
 * still recognise a ref update that landed before an ambiguous response.
 *
 * Every unresolved identity is retained, because a batch can be reshaped between failed attempts
 * and then reshaped back; evicting one would mint a new key for a request that may already have
 * committed server-side. Retention ends at the first confirmed apply, or when the tab closes —
 * both well inside the lifetime of the server-side recovery marker.
 */
export function resolveSuggestionOperationId(pullRequestPath: string, identity: string): string {
  const key = getSuggestionOperationStorageKey(pullRequestPath)
  const operations = loadRetainedOperations(key)

  const retained = operations.find(operation => operation.identity === identity)
  if (retained) return retained.id

  const id = crypto.randomUUID()
  const next = [{identity, id}, ...operations]
  retainedOperationsCache.set(key, next)
  storage.setItem(key, {version: SUGGESTION_OPERATION_STORAGE_VERSION, operations: next})
  return id
}

/** Called once the server has confirmed the apply, so the next batch starts a new operation. */
export function clearSuggestionOperation(pullRequestPath: string): void {
  const key = getSuggestionOperationStorageKey(pullRequestPath)
  retainedOperationsCache.delete(key)
  storage.removeItem(key)
}
